import { JSX_NODE_MAPPINGS } from "@builder/utils/JSXtoBuilder/jsxMapping";
import { DynamicDataContext } from "@common/build/activeEntityType";
import { generateText } from "ai";
import { model } from "../../useAiChat";
import { getGlobalColors } from "../globalTools";
import { findDynamicData } from "../metaTools";
import { SearchResult } from "../../resources/searchResources";

const allowedChildrenInRepeaterDiv = JSX_NODE_MAPPINGS.repeaterDiv?.allowedChildNames?.join(", ");


export const dynamicGridPrompt = async (
  gridType: "product" | "order" | "customer",
  existingGridJSX: string | undefined,
  instructions: string,
  templates: string[]
): Promise<string> => {
    const gridMap = {
        product: {
            elementExampleName: "ProductList or ProductGrid",
            queryExample: `query={{dataType:"products",limit:25,order:{orderBy:"name",orderDirection:"descending"}}}`,
            context: "product"
        },
        order: {
            elementExampleName: "OrderList",
            queryExample: `query={{dataType:"orders",limit:25,queries:{orderSource:"all","orderStatus":"all","orderDateRange":"all","orderCustomer":"all","orderOutlet":"all","orderStation":"all","orderServedBy":"all"}}}`,
            context: "order"
        },
        customer: {
            elementExampleName: "CustomerList",
            queryExample: `query={{dataType:"customers",limit:25,queries:{},order:{orderBy:"firstName A-Z",orderDirection:"descending"}}}`,
            context: "customer"
        }
    } as const;

    const { elementExampleName, queryExample, context } = gridMap[gridType];
    const colorScheme = getGlobalColors();

    const dynamicDataResults = await findDynamicData(undefined, context as DynamicDataContext);

    let dynamicDataSection = "";
    let exampleKey = "dynamicKey";
    if (!Array.isArray(dynamicDataResults)) {
        console.error("Dynamic data results are not an array", dynamicDataResults);
        throw new Error("Dynamic data results are not an array");
    }

    if (dynamicDataResults.length > 0) {
        exampleKey = dynamicDataResults[0].key;
    }
    const keysList = dynamicDataResults.map(item =>item.key)
    dynamicDataSection = `\nAvailable dynamic data keys:\n${keysList}\n`;

    const editingSegment = existingGridJSX
      ? `Here is the grid you are editing:\n${existingGridJSX}\nThe user is asking to apply the following instructions to it:\n${instructions}\nTo make it look professional, you should get inspired and strictly follow some template examples for styles:\n${templates.join("\n")}\n`
      : `Here are some templates you can take from:\n${templates.join("\n")}\nUse one of these templates as inspiration, and adjust it to the instructions. Keep the design as close to the template as possible while following the instructions: ${instructions}\n. MAKE S`;

    return `
You're a confident design assistant designed to help the user build their page using our JSX component library.
Your one and only job is to edit the dynamic ${gridType} grid ${gridType === "product" ? "or list" : ""} presented to you.

Functionality explanation:
- The repeaterDiv acts as its own data context. Each time it repeats, it is bound to a single record from the list (${gridType}).
- When a user clicks/taps on an item inside the repeaterDiv that item becomes the “selected” ${gridType} in the global state.
- Other components that live outside the repeaterDiv will then show data for the selected item.
- Because of that, any dynamic data key you put INSIDE the repeaterDiv refers to the repeated item, while the same key OUTSIDE refers to the currently selected record.

Query example for this grid:
${queryExample}
for now stick to that, unless you receive something different - then just keep it as is.

Elements inside:
- Unless you receive something different, the only element allowed inside the dynamic grid is repeaterDiv. If there are already other elements, you may leave them as is.
- If elements have ids, DO NOT CHANGE THEM. PRESERVE ALL IDS. The ONLY exception is if you change a ProductList to a ProductGrid or vice versa, in which case you must change or omit the id.

Dynamic data usage:
- Inside a Text node (e.g. <Text dynamicData={${exampleKey}}/>) you can embed a dynamic data key to show data that changes for each repetition. The value will be different for each item rendered by the repeaterDiv.
- in image node, you can also use dynamic data (only if applicable - if you have in the available dynamic data keys an image key).
- You may with text - Use prepend/append props to add labels (e.g., prepend="Price: " for prices)
- prepend / append: both have {value, style} to appear before / after dynamic data. They styles could be used to make them different colors or sizes than the dynamic data text.
Do not ever prepend / append currency symbols. It happens automatically.

${dynamicDataSection}

YOU MAY ONLY USE THESE DYNAMIC DATA KEYS. DO NOT MAKE UP ANYTHING.

Design:
Use only inline styles (no Tailwind, no JS, no libraries).
Keep your changes minimal!!


You are highly encouraged to use these colors: ${JSON.stringify(colorScheme)}
to use them, just use the FULL var name in your CSS.
Make your designs use these colors.


${editingSegment}


- Its structure is always:
<${elementExampleName}> 
  <repeaterDiv ...>
    [valid elements, like: ${allowedChildrenInRepeaterDiv}]
  </repeaterDiv>
</${elementExampleName}>



MUST RETURN JSX STRING.
NO MARKDOWN, NO CODE FENCES, NO ESCAPED CHARACTERS, NO THINGS THAT ARE NOT STRING JSX. NO NEWLINES.
JUST <JSX>...</>
`;
};



export const editDynamicGrid = async ({
  gridType,
  existingComponentsJSX,
  instructions,
  templates,
}: {
  gridType: "product" | "order" | "customer";
  existingComponentsJSX?: string;
  instructions: string;
  templates: SearchResult<string>[];
}): Promise<string> => {
    const inspirationTemplates = templates.map(template => {
        return `
        ${template.description}:\n
        ${template.value}\n
        `
    });
  const prompt = await dynamicGridPrompt(gridType, existingComponentsJSX, instructions, inspirationTemplates);

  const { text } = await generateText({
    model,
    prompt,
  });

  return text;
};